import '../styling/experience.scss';
import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { FaCode, FaBug, FaBriefcase } from "react-icons/fa";

const experience = [
    {
      role: "Quality Automation Engineer",
      company: "Estée Lauder Companies via HireTalent",
      date: "July 2023 – Present",
      type: "qa",
      impact:
        "Improved test automation coverage and reduced manual QA effort across web platforms",
        bullets: [
            "Developed and maintained end-to-end tests using Playwright",
            "Reduced flaky tests and improved CI reliability",
            "Collaborated with developers to ensure testable and stable releases",
        ],
        tech: ["LambdaTest", "Playwright", "JavaScript", "CI/CD"],
    },
    {
        role: "Software Engineer (Release/Test)",
        company: "Twitter, Inc.",
        date: "May 2021 – January 2023",
        type: "qa",
        impact:
            "Enhanced release quality by building automation workflows and improving testing processes",
        bullets: [
            "Built automated testing workflows for web applications",
            "Identified and resolved issues before production releases",
            "Worked cross-functionally to improve deployment reliability",
        ],
        tech: ["Automation", "Testing", "Web Apps"],
    },
    {
        role: "Software Engineer (DevOps / Intern)",
        company: "Hewlett Packard Enterprise",
        date: "May 2020 – April 2021",
        type: "dev",
        impact:
            "Supported infrastructure and development workflows to improve system reliability",
        bullets: [
            "Assisted in DevOps pipeline improvements",
            "Supported deployment and monitoring processes",
            "Worked with engineering teams on system stability",
        ],
        tech: ["DevOps", "CI/CD", "Cloud", "Snaplogic"],
    },
    {
        role: "Intern",
        company: "Hewlett Packard Enterprise",
        date: "May 2020 – Jan 2021",
        type: "intern",
        impact:
            "Contributed to engineering efforts through feature support and development tasks",
        bullets: [
            "Assisted in building and testing internal tools",
            "Collaborated with engineers on feature development",
        ],
        tech: ["Engineering", "Support", "React"],
    },
];

function getIcon(type) {
    switch (type) {
        case "dev":
            return <FaCode />;
        case "qa":
            return <FaBug />;
        default:
            return <FaBriefcase />;
    }
}

export default function Timeline() {
    const containerRef = useRef(null);

    const { scrollYProgress } = useScroll({
        target: containerRef,
        offset: ["start 0.9", "end 0.2"],
    });

    const scaleY = useTransform(scrollYProgress, [0, 1], [0, 1]);

    return (
        <div className="timeline-wrapper" ref={containerRef}>
            {/* animated vertical line */}
            <motion.div className="timeline-line" style={{ scaleY }} />
            <div className="timeline">
                {experience.map((item, i) => (
                    <motion.div
                        key={i}
                        className="timeline-item"
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.3 }}
                        transition={{
                            duration: 0.6,
                            delay: i * 0.1, // stagger effect
                            ease: "easeOut",
                        }}
                    >
                        {/* dot + icon */}
                        <div className="timeline-marker">
                            <div className="timeline-dot" />
                            <div className="timeline-icon">{getIcon(item.type)}</div>
                        </div>

                        <div className="timeline-content">
                            <h3>
                                {item.role} <span>@ {item.company}</span>
                            </h3>
                            <p className="timeline-date">{item.date}</p>
                            <p className="timeline-impact">⭐ {item.impact}</p>

                            <ul className="timeline-bullets">
                                {item.bullets.map((b, idx) => (
                                    <li key={idx}>{b}</li>
                                ))}
                            </ul>

                            <div className="timeline-tech">
                                {item.tech.join(" • ")}
                            </div>
                        </div>
                    </motion.div>
                ))}
            </div>
            {/* </motion.div> */}
        </div>

    );
}